package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.colaboradores.ColabDados
import br.com.htms.inspeccaocampo.data.colaboradores.Colaborador
import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.data.colaboradores.DocsMatriz
import br.com.htms.inspeccaocampo.data.colaboradores.SituacaoDoc
import br.com.htms.inspeccaocampo.data.colaboradores.StatusColab
import br.com.htms.inspeccaocampo.data.colaboradores.StatusDoc

/** Modelo "MATRIZ DE COLABORADORES": PAINEL, PENDÊNCIAS, EFETIVO CLT, EFETIVO PJ e a base de vencimentos. */
object PlanilhaColaboradores {
    private data class LinhaVenc(val c: Colaborador, val doc: String, val obrig: Boolean, val s: SituacaoDoc)

    fun build(d: ColabDados): ByteArray {
        val hoje = DatasColab.hoje()
        val hojeIso = DatasColab.isoDeDias(hoje)
        val regras = d.regras
        val todos = d.colaboradores.sortedBy { it.nome.lowercase() }

        fun abaEfetivo(nome: String, pj: Boolean): AbaDados {
            val lista = todos.filter { (it.vinculo == "PJ") == pj }
            val cols = buildList {
                add(ColPlan(if (pj) "CNPJ" else "MTR", if (pj) 20 else 9))
                add(ColPlan("COLABORADOR", 40))
                add(ColPlan("FUNÇÃO", 34))
                add(ColPlan("SITUAÇÃO", 14))
                add(ColPlan("ADMISSÃO", 12, TipoCol.DATA))
                add(ColPlan("NASCIMENTO", 12, TipoCol.DATA))
                if (pj) add(ColPlan("CTPS", 14))
                add(ColPlan("RG", 14))
                add(ColPlan("CPF", 16))
                add(ColPlan("SETOR / GHE", 18))
                DocsMatriz.todos.forEach { doc ->
                    add(ColPlan(doc.rotulo.uppercase(), 12, if (doc.marcacao) TipoCol.TEXTO else TipoCol.DATA))
                    if (!doc.marcacao) add(ColPlan("${doc.rotulo.uppercase()} SITUAÇÃO", 12))
                }
                add(ColPlan("DIR.DEF.", 10))
                add(ColPlan("CAPACITAÇÃO", 14))
                add(ColPlan("CNH", 8))
                add(ColPlan("CNH VALIDADE", 12, TipoCol.DATA))
                add(ColPlan("CNH SITUAÇÃO", 14))
                add(ColPlan("PIOR SITUAÇÃO", 14))
                add(ColPlan("OBSERVAÇÕES", 34))
            }
            val linhas = lista.map { c ->
                buildList<Any?> {
                    add(if (pj) c.cnpj else c.matricula.toIntOrNull() ?: c.matricula)
                    add(c.nome)
                    add(c.funcao)
                    add(c.situacao.uppercase())
                    add(PlanData.de(c.admissao))
                    add(PlanData.de(c.nascimento))
                    if (pj) add(c.ctps)
                    add(c.rg)
                    add(c.cpf)
                    add(c.setorGhe)
                    DocsMatriz.todos.forEach { doc ->
                        val v = c.docs[doc.key].orEmpty()
                        if (doc.marcacao) {
                            add(v)
                        } else {
                            add(PlanData.de(v) ?: v)
                            add(StatusColab.doc(c, doc.key, regras, hoje).status.rotulo)
                        }
                    }
                    add(c.dirdef)
                    add(c.capacitacao)
                    add(c.cnh.categoria)
                    add(PlanData.de(c.cnh.validade))
                    add(StatusColab.cnh(c, regras, hoje).status.rotulo)
                    add(StatusColab.pior(c, regras, hoje).rotulo)
                    add(c.obs)
                }
            }
            return PlanKit.abaDados(
                nome, "Efetivo ${if (pj) "PJ" else "CLT"} — matriz de documentos",
                "Datas de realização e situação de cada documento (regras do portal: crítico ≤ ${regras.crit} dias, atenção ≤ ${regras.aten} dias).",
                cols, linhas, cor = if (pj) "7E57C2" else "1565C0"
            )
        }
        val clt = abaEfetivo("EFETIVO CLT", pj = false)
        val pj = abaEfetivo("EFETIVO PJ", pj = true)

        val base = todos.filter { it.noQuadro(hojeIso) }.flatMap { c ->
            DocsMatriz.todos.map { doc -> LinhaVenc(c, doc.rotulo, regras.doc(doc.key).obrig, StatusColab.doc(c, doc.key, regras, hoje)) } +
                c.outros.map { extra -> LinhaVenc(c, extra.nome, false, StatusColab.outro(extra, regras, hoje)) } +
                LinhaVenc(c, "CNH", false, StatusColab.cnh(c, regras, hoje))
        }.filter { it.s.status != StatusDoc.NA && it.s.status != StatusDoc.SEM_REGISTRO && it.s.status != StatusDoc.NAO_INFORMADO }
        val colsVenc = listOf(
            ColPlan("VÍNCULO", 9), ColPlan("MTR / CNPJ", 18), ColPlan("COLABORADOR", 38), ColPlan("FUNÇÃO", 30),
            ColPlan("DOCUMENTO", 12), ColPlan("OBRIGATÓRIO", 12), ColPlan("REALIZADO", 12, TipoCol.DATA),
            ColPlan("VENCIMENTO", 12, TipoCol.DATA), ColPlan("DIAS P/ VENCER", 12, TipoCol.NUMERO), ColPlan("SITUAÇÃO", 14)
        )
        val cVenc = PlanKit.colRef(colsVenc.indexOfFirst { it.titulo == "VENCIMENTO" })
        fun linhaVenc(v: LinhaVenc): List<Any?> = listOf(
            v.c.vinculo, if (v.c.vinculo == "PJ") v.c.cnpj else v.c.matricula, v.c.nome, v.c.funcao,
            v.doc, v.obrig, PlanData.de(v.s.realizado), PlanData.de(v.s.vencimento),
            if (v.s.vencimento.isBlank()) null else Fx("IF(ISNUMBER($cVenc{r}),$cVenc{r}-TODAY(),\"\")", v.s.dias ?: ""),
            v.s.status.rotulo
        )
        val ordem = listOf(StatusDoc.VENCIDO, StatusDoc.PENDENTE, StatusDoc.CRITICO, StatusDoc.ATENCAO, StatusDoc.VERIFICAR, StatusDoc.VALIDO)
        val venc = PlanKit.abaDados(
            "VENCIMENTOS", "Base de vencimentos — colaboradores ativos",
            "Uma linha por colaborador e documento. Filtre por vínculo, função, documento ou situação: o PAINEL acompanha o filtro.",
            colsVenc,
            base.sortedWith(compareBy({ ordem.indexOf(it.s.status).let { i -> if (i < 0) 99 else i } }, { it.s.dias ?: Int.MAX_VALUE }, { it.c.nome }))
                .map(::linhaVenc),
            cor = "26A69A"
        )
        val pend = base.filter { it.s.status == StatusDoc.VENCIDO || it.s.status == StatusDoc.PENDENTE || it.s.status == StatusDoc.CRITICO }
        val pendencias = PlanKit.abaDados(
            "PENDÊNCIAS", "Pendências · documentos vencidos, críticos ou sem registro obrigatório",
            "${pend.size} pendência(s) em ${pend.map { it.c.id }.distinct().size} colaborador(es) ativo(s).",
            colsVenc,
            pend.sortedWith(compareBy({ ordem.indexOf(it.s.status) }, { it.s.dias ?: Int.MAX_VALUE }, { it.c.nome })).map(::linhaVenc),
            cor = "C62828"
        )

        val ativos = todos.filter { it.noQuadro(hojeIso) }
        val sit = venc.faixa("SITUAÇÃO")
        val vis = venc.visiveis()
        fun conta(s: StatusDoc) = base.count { it.s.status == s }.toDouble()
        val emDia = base.count { it.s.status.emDia }
        val avaliados = emDia + base.count { it.s.status == StatusDoc.VENCIDO || it.s.status == StatusDoc.PENDENTE }
        val emDiaFx = "SUMPRODUCT((($sit=\"Válido\")+($sit=\"Atenção\")+($sit=\"Crítico\"))*$vis)"
        val avaliadosFx = "SUMPRODUCT((($sit=\"Válido\")+($sit=\"Atenção\")+($sit=\"Crítico\")+($sit=\"Vencido\")+($sit=\"Pendente\"))*$vis)"
        fun ativosNa(aba: AbaDados) =
            "COUNTIF(${aba.faixa("SITUAÇÃO")},\"ATIVO*\")+COUNTIFS(${aba.faixa("SITUAÇÃO")},\"\",${aba.faixa("COLABORADOR")},\"<>\")"
        val kpis = listOf(
            KpiPlan("Colaboradores ativos", "${ativosNa(clt)}+${ativosNa(pj)}", ativos.size.toDouble()),
            KpiPlan("Vencidos", venc.contarSe("SITUAÇÃO", "Vencido"), conta(StatusDoc.VENCIDO), SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Pendentes", venc.contarSe("SITUAÇÃO", "Pendente"), conta(StatusDoc.PENDENTE), SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Críticos (≤ ${regras.crit} dias)", venc.contarSe("SITUAÇÃO", "Crítico"), conta(StatusDoc.CRITICO), SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Atenção (≤ ${regras.aten} dias)", venc.contarSe("SITUAÇÃO", "Atenção"), conta(StatusDoc.ATENCAO), SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Válidos", venc.contarSe("SITUAÇÃO", "Válido"), conta(StatusDoc.VALIDO), SharedOoxmlWriter.STYLE_CRIT_BAIXA),
            KpiPlan(
                "Conformidade", "IFERROR($emDiaFx/$avaliadosFx,0)",
                if (avaliados == 0) 0.0 else emDia.toDouble() / avaliados, SharedOoxmlWriter.STYLE_KPI_PCT
            ),
            pendencias.faixa("COLABORADOR").let { r ->
                KpiPlan(
                    "Colab. com pendência", "SUMPRODUCT(($r<>\"\")/COUNTIF($r,$r&\"\"))",
                    pend.map { it.c.id }.distinct().size.toDouble(), SharedOoxmlWriter.STYLE_CRIT_ALTA
                )
            }
        )
        val doc = venc.faixa("DOCUMENTO")
        val problemas = base.filter { it.s.status == StatusDoc.VENCIDO || it.s.status == StatusDoc.PENDENTE || it.s.status == StatusDoc.CRITICO }
        val blocos = listOf(
            BlocoPlan("Situação dos documentos", "Situação", PlanKit.contagem(base) { it.s.status.rotulo }, { venc.contarSe("SITUAÇÃO", it) }, SharedExcelChartType.PIE),
            BlocoPlan(
                "Vencidos, pendentes e críticos por documento", "Documento",
                PlanKit.contagem(problemas) { it.doc },
                { "SUMPRODUCT(($doc=${PlanKit.texto(it)})*(($sit=\"Vencido\")+($sit=\"Pendente\")+($sit=\"Crítico\"))*$vis)" }
            ),
            BlocoPlan(
                "Vencimentos nos próximos 60 dias por documento", "Documento",
                PlanKit.contagem(base.filter { (it.s.dias ?: -1) in 0..60 }) { it.doc },
                { "SUMPRODUCT(($doc=${PlanKit.texto(it)})*(${venc.faixa("DIAS P/ VENCER")}>=0)*(${venc.faixa("DIAS P/ VENCER")}<=60)*(${venc.faixa("DIAS P/ VENCER")}<>\"\")*$vis)" }
            ),
            BlocoPlan(
                "Colaboradores ativos por vínculo", "Vínculo",
                PlanKit.contagem(ativos) { it.vinculo },
                { if (it == "PJ") ativosNa(pj) else ativosNa(clt) }, SharedExcelChartType.PIE
            ),
            BlocoPlan(
                "Colaboradores ativos por função", "Função",
                PlanKit.contagem(ativos) { it.funcao },
                { "COUNTIFS(${clt.faixa("FUNÇÃO")},${PlanKit.texto(it)},${clt.faixa("SITUAÇÃO")},\"ATIVO*\")+COUNTIFS(${pj.faixa("FUNÇÃO")},${PlanKit.texto(it)},${pj.faixa("SITUAÇÃO")},\"ATIVO*\")" },
                formulaTotal = "${ativosNa(clt)}+${ativosNa(pj)}"
            )
        )
        val painel = PlanKit.dashboard(
            "PAINEL", "Painel de vencimentos · ASO e treinamentos",
            "Filtre a aba VENCIMENTOS (vínculo, função, documento, situação) e o painel acompanha. Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos
        )
        return PlanKit.gerar(listOf(painel, pendencias.sheet, clt.sheet, pj.sheet, venc.sheet))
    }
}
